import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box } from '@zillow/constellation-10';
import { useSatelliteWebLandlordConversationHandler_search } from '@zg-rentals/ts-satellite-client-react-query';
import type { SatelliteWebLandlordConversationSearchMatchingConversationDto } from '@zg-rentals/ts-satellite-client';
import {
  SatelliteConversationFolderTypeV2,
  SatelliteConversationReadRepliedStatusTypeV2,
  SatelliteConversationRenterProgressStatusTypeV2,
} from '@zg-rentals/ts-satellite-client';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import { MAX_OLD_CONVERSATION_DAYS } from '../../constants/ConfigConstants';
import {
  inboxPageConversationIdToFilterByStore,
  inboxPageConversationSearchTextStore,
  inboxPageIsLoadingStore,
  inboxPageShowEmptyContainerStore,
} from '../../lib/inboxSearchListStores';
import { isConversationSearchBarFocusedStore } from '../../lib/inboxAddMessageStores';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import getValueFromEnum from '../../utils/getValueFromEnum';
import { getDateRangesInMsFromDaysBack } from '../../utils/getDateRangesInMsFromDaysBack';
import getIsPhoneNumber from '../../utils/getIsPhoneNumber';
import getFormattedSearchText from '../../utils/getFormattedSearchText';
import type { SearchResultItem } from '../SearchBar';
import SearchBar, {
  ERROR_MESSAGE,
  filterSpecialCharacters,
  MAX_CHARACTERS_MESSAGE,
  MAX_LENGTH_SEARCH_TEXT,
} from '../SearchBar';
import { rentalClient } from '../../httpClient';

const SEARCH_PAGE_SIZE = 10;

const formatConversationSearchResults = (
  conversationSearchResults: Array<SatelliteWebLandlordConversationSearchMatchingConversationDto>,
  showRenterPhoneNumber = false,
): Array<SearchResultItem> => {
  return conversationSearchResults.map((conversationSearchResult) => {
    const { conversationId, folder, listingAlias, renterName, renterPhone, streetAddress } =
      conversationSearchResult || {};
    const label = showRenterPhoneNumber ? `${renterName ?? ''} ${renterPhone ?? ''}` : (renterName ?? '');

    return {
      meta: streetAddress ?? '',
      value: renterName ?? '',
      label,
      conversationId: conversationId ?? '',
      listingAlias: listingAlias ?? '',
      folder: folder ?? '',
    };
  });
};

export default function ConversationSearchBar() {
  const { getFiltersQueryString, updatePath, removeQueryParam } = useInboxRouterHelper();
  const { mobileView } = useContext(InboxPageContext);

  const inboxPageConversationSearchText = inboxPageConversationSearchTextStore.useValue();
  const inboxPageIsLoading = inboxPageIsLoadingStore.useValue();

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [helperText, setHelperText] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);
  const [offset, setOffset] = useState(0);
  const [accumulatedResults, setAccumulatedResults] = useState<
    Array<SatelliteWebLandlordConversationSearchMatchingConversationDto>
  >([]);
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false);
  const lastAppliedOffsetRef = useRef<number>(-1);

  const { startDateMs: startTimestampMs, endDateMs: endTimestampMs } =
    getDateRangesInMsFromDaysBack(MAX_OLD_CONVERSATION_DAYS);

  const formattedSearchText = debouncedSearchText ? getFormattedSearchText(debouncedSearchText) : '';
  const searchPostData = {
    searchText: formattedSearchText,
    offset,
    limit: SEARCH_PAGE_SIZE,
    startTimestampMs,
    endTimestampMs,
  };

  const { data: searchData, error } = useSatelliteWebLandlordConversationHandler_search(searchPostData, {
    httpClient: rentalClient,
    enabled: formattedSearchText.length > 1,
    meta: {
      context: {
        api: 'conversationSearch',
        details: 'ConversationSearchBar - Error fetching conversation search result data',
        searchText: formattedSearchText,
        offset,
        startTimestampMs,
        endTimestampMs,
      },
    },
  });

  // Debounce search text (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Reset offset and accumulated results when search text changes
  useEffect(() => {
    setOffset(0);
    setAccumulatedResults([]);
    lastAppliedOffsetRef.current = -1;
  }, [debouncedSearchText]);

  // Sync accumulated results and hasMore from query data. Only update when the query is active
  // (formattedSearchText.length > 1) so that clearing the search does not repopulate from stale
  // React Query cache. Only update the search stores when the search has not been cleared.
  useEffect(() => {
    if (!searchData || formattedSearchText.length <= 1) return;

    if (offset === 0) {
      setAccumulatedResults(searchData.matchingConversations ?? []);
      setHasMoreSearchResults(searchData.hasMore ?? false);
      lastAppliedOffsetRef.current = 0;
      const inputHasSearchText = searchText.trim().length > 0;
      const searchTextNotCleared = inboxPageConversationSearchText !== '';
      if (inputHasSearchText && searchTextNotCleared) {
        inboxPageShowEmptyContainerStore.set(!(searchData.matchingConversations?.length ?? 0));
        inboxPageConversationSearchTextStore.set(searchText);
      }
    } else if (offset > 0 && lastAppliedOffsetRef.current !== offset) {
      setAccumulatedResults((prev) => [...prev, ...(searchData.matchingConversations ?? [])]);
      setHasMoreSearchResults(searchData.hasMore ?? false);
      lastAppliedOffsetRef.current = offset;
    }
  }, [searchData, offset, searchText, formattedSearchText.length, inboxPageConversationSearchText]);

  useEffect(() => {
    if (error) {
      setHelperText(ERROR_MESSAGE);
      inboxPageShowEmptyContainerStore.set(true);
    }
  }, [error]);

  useEffect(() => {
    // Persist search text (i.e. when filters are applied)
    if (inboxPageConversationSearchText) {
      setSearchText(inboxPageConversationSearchText);
    }
  }, []);

  useEffect(() => {
    // Reset search text (Search text can be reset from ConversationList's EmptyContainer)
    if (!inboxPageConversationSearchText) {
      setSearchText('');
    }

    // Disable search while inbox page is loading
    if (!inboxPageIsLoading) {
      setIsDisabled(false);
    }
  }, [inboxPageConversationSearchText, inboxPageIsLoading]);

  const handleChangeInputText = (inputText: string) => {
    let formattedInputText = filterSpecialCharacters(inputText);
    setSearchText(inputText);

    if (helperText) {
      setHelperText('');
    }

    if (inputText.length === MAX_LENGTH_SEARCH_TEXT) {
      setHelperText(MAX_CHARACTERS_MESSAGE);
    }

    if (!formattedInputText) {
      // If the user clears the search bar, clear the conversationIdToFilterBy store
      // ConversationList component will refetch conversations without the 660 daysBack param
      removeQueryParam('daysBack');
      inboxPageConversationIdToFilterByStore.set('');
      inboxPageShowEmptyContainerStore.set(false);
      inboxPageConversationSearchTextStore.set('');
    } else if (formattedInputText.length > 1) {
      if (formattedInputText.length > MAX_LENGTH_SEARCH_TEXT) {
        formattedInputText = formattedInputText.substring(0, MAX_LENGTH_SEARCH_TEXT);
      }
      // Set the store as the user types so the sync effect can use it to avoid overwriting after "Clear search"
      inboxPageConversationSearchTextStore.set(inputText);
    }
  };

  const handleSelectRenterFromSearch = (selectedRenter: SearchResultItem) => {
    const { conversationId, folder, listingAlias, value } = selectedRenter;
    const renterNameTrimmed = value.substring(0, MAX_LENGTH_SEARCH_TEXT);

    setSearchText(renterNameTrimmed);
    inboxPageConversationSearchTextStore.set(renterNameTrimmed);
    inboxPageConversationIdToFilterByStore.set(conversationId || '');
    isConversationSearchBarFocusedStore.set(false);

    updatePath({
      conversationId,
      listingAlias,
      byProperty: undefined,
      filtersQueryString: getFiltersQueryString({
        byProperty: false,
        daysBack: MAX_OLD_CONVERSATION_DAYS,
        folder: getValueFromEnum(folder.toUpperCase(), SatelliteConversationFolderTypeV2),
        mobileView: mobileView || InboxPageMobileView.ConversationList,
        readRepliedStatus: SatelliteConversationReadRepliedStatusTypeV2.SHOW_ALL,
        renterProgressStatus: SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL,
      }),
    });
  };

  const handleFetchMoreSearchResults = useCallback((searchTextForMore: string, nextOffset: number) => {
    setOffset(nextOffset);
  }, []);

  const handleFocus = () => isConversationSearchBarFocusedStore.set(true);

  const handleBlur = () => isConversationSearchBarFocusedStore.set(false);

  const isPhoneNumber = getIsPhoneNumber(searchText);
  const formattedSearchResults = formatConversationSearchResults(accumulatedResults, isPhoneNumber);

  return (
    <Box
      css={{
        borderBottom: '1px solid',
        borderBottomColor: 'border.muted',
        padding: 'default',
      }}
    >
      <SearchBar
        isDisabled={isDisabled}
        onSelectSearchItem={handleSelectRenterFromSearch}
        placeholder="Search by name, phone #"
        searchText={searchText}
        fetchMoreSearchResults={handleFetchMoreSearchResults}
        hasError={!!error}
        hasMoreSearchResults={hasMoreSearchResults}
        helperText={helperText}
        onChangeInputText={handleChangeInputText}
        searchResults={formattedSearchResults}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    </Box>
  );
}
